import type {
  GeoJSONSource,
  Map as MapLibreMap,
  MapLayerMouseEvent,
  MapMouseEvent,
  MapTouchEvent,
} from 'maplibre-gl';
import { closeRing, moveVertex, simplify, type Pos, type Poly } from './areaGeometry';

type Feature = {
  type: 'Feature';
  properties: Record<string, unknown>;
  geometry: { type: 'Point' | 'LineString' | 'Polygon'; coordinates: unknown };
};
type SourceData = Parameters<GeoJSONSource['setData']>[0];

export type DrawMode = 'edit' | 'freehand' | 'polygon' | 'probe' | 'pick';

const SRC = 'area-edit';
const DRAFT = 'area-draft';

/**
 * Area drawing on MapLibre without extra dependencies:
 * - freehand ("pencil"): press and drag, the stroke is simplified (Douglas–Peucker, 2 px) into a polygon;
 * - polygon: click to add corners, double-click or click the first corner to close, Escape cancels;
 * - edit: drag corners; click a part to select it (for deletion);
 * - probe: click to report a position (adoption preview);
 * - pick: click to report a position (boundary suggestion of the commune there).
 * Several parts are allowed (MultiPolygon). The owner receives every change through onChange.
 */
export class DrawTool {
  private parts: Poly[] = [];
  private history: Poly[][] = [];
  private mode: DrawMode = 'edit';
  private stroke: { x: number; y: number; ll: Pos }[] = [];
  private polygon: Pos[] = [];
  private drag: { p: number; r: number; i: number } | null = null;
  private selected: number | null = null;
  private readonly off: (() => void)[] = [];
  private mergeSeq = 0;

  constructor(
    private readonly map: MapLibreMap,
    private readonly colors: { fill: string; line: string; vertex: string; selected: string },
    private readonly onChange: (s: { parts: Poly[]; selected: number | null; canUndo: boolean }) => void,
    private readonly onProbe: (p: Pos) => void,
    /** Merges overlapping parts (server-side union); repeated pencil strokes become one area. */
    private readonly normalize?: (parts: Poly[]) => Promise<Poly[] | null>,
    private readonly onPick?: (p: Pos) => void,
  ) {
    map.addSource(SRC, { type: 'geojson', data: this.fc() as unknown as SourceData });
    map.addSource(DRAFT, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addLayer({
      id: 'area-fill',
      type: 'fill',
      source: SRC,
      filter: ['==', ['get', 'k'], 'part'],
      paint: {
        'fill-color': ['case', ['get', 'sel'], colors.selected, colors.fill],
        'fill-opacity': 0.22,
      },
    });
    map.addLayer({
      id: 'area-line',
      type: 'line',
      source: SRC,
      filter: ['==', ['get', 'k'], 'part'],
      paint: { 'line-color': ['case', ['get', 'sel'], colors.selected, colors.line], 'line-width': 2.5 },
    });
    map.addLayer({
      id: 'area-vertex',
      type: 'circle',
      source: SRC,
      filter: ['==', ['get', 'k'], 'v'],
      paint: {
        'circle-radius': 5,
        'circle-color': '#ffffff',
        'circle-stroke-color': colors.vertex,
        'circle-stroke-width': 2,
      },
    });
    map.addLayer({
      id: 'area-draft',
      type: 'line',
      source: DRAFT,
      paint: { 'line-color': colors.line, 'line-width': 2, 'line-dasharray': [2, 1.5] },
    });
    map.addLayer({
      id: 'area-draft-pt',
      type: 'circle',
      source: DRAFT,
      filter: ['==', ['geometry-type'], 'Point'],
      paint: { 'circle-radius': 4, 'circle-color': colors.line },
    });

    const on = <E>(type: string, fn: (e: E) => void, layer?: string) => {
      if (layer) map.on(type as 'click', layer, fn as never);
      else map.on(type as 'click', fn as never);
      this.off.push(() =>
        layer ? map.off(type as 'click', layer, fn as never) : map.off(type as 'click', fn as never),
      );
    };
    on<MapMouseEvent | MapTouchEvent>('mousedown', (e) => this.down(e));
    on<MapTouchEvent>('touchstart', (e) => e.points.length === 1 && this.down(e));
    on<MapMouseEvent | MapTouchEvent>('mousemove', (e) => this.move(e));
    on<MapTouchEvent>('touchmove', (e) => this.move(e));
    on<MapMouseEvent>('click', (e) => this.click(e));
    on<MapMouseEvent>('dblclick', (e) => {
      if (this.mode === 'polygon') {
        e.preventDefault();
        this.finishPolygon();
      }
    });
    on<MapLayerMouseEvent>('mouseenter', () => this.cursor(), 'area-vertex');
    on<MapLayerMouseEvent>('mouseleave', () => this.cursor(), 'area-vertex');
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.cancelDraft();
      if (e.key === 'Enter' && this.mode === 'polygon') this.finishPolygon();
    };
    window.addEventListener('keydown', key);
    this.off.push(() => window.removeEventListener('keydown', key));
    // Release is taken from the window: the pointer may leave the canvas while drawing or dragging.
    const release = () => this.up();
    window.addEventListener('mouseup', release);
    window.addEventListener('touchend', release);
    window.addEventListener('touchcancel', release);
    this.off.push(() => {
      window.removeEventListener('mouseup', release);
      window.removeEventListener('touchend', release);
      window.removeEventListener('touchcancel', release);
    });
    map.doubleClickZoom.disable();
  }

  destroy() {
    this.off.forEach((f) => f());
  }

  setParts(parts: Poly[], record = false) {
    if (record) this.history.push(this.parts);
    this.parts = parts;
    if (this.selected !== null && this.selected >= parts.length) this.selected = null;
    this.render();
  }

  setMode(mode: DrawMode) {
    this.cancelDraft();
    this.mode = mode;
    // Drawing strokes and dragging corners must not pan the map.
    if (mode === 'freehand') this.map.dragPan.disable();
    else this.map.dragPan.enable();
    this.cursor();
    this.render();
  }

  undo() {
    const prev = this.history.pop();
    if (prev) this.setParts(prev);
  }

  deleteSelected() {
    if (this.selected === null) return;
    const i = this.selected;
    this.selected = null;
    this.setParts(
      this.parts.filter((_, k) => k !== i),
      true,
    );
  }

  clear() {
    if (this.parts.length) this.setParts([], true);
  }

  // ---------------------------------------------------------------------------------------------------------------

  private down(e: MapMouseEvent | MapTouchEvent) {
    if (this.mode === 'freehand') {
      e.preventDefault();
      this.stroke = [{ x: e.point.x, y: e.point.y, ll: [e.lngLat.lng, e.lngLat.lat] }];
      return;
    }
    if (this.mode === 'edit') {
      const hit = this.map.queryRenderedFeatures(
        [
          [e.point.x - 6, e.point.y - 6],
          [e.point.x + 6, e.point.y + 6],
        ],
        { layers: ['area-vertex'] },
      )[0];
      if (!hit) return;
      e.preventDefault();
      const { p, r, i } = hit.properties as { p: number; r: number; i: number };
      this.history.push(this.parts);
      this.drag = { p, r, i };
      this.map.dragPan.disable();
    }
  }

  private move(e: MapMouseEvent | MapTouchEvent) {
    const ll: Pos = [e.lngLat.lng, e.lngLat.lat];
    if (this.mode === 'freehand' && this.stroke.length) {
      const last = this.stroke.at(-1)!;
      if (Math.hypot(e.point.x - last.x, e.point.y - last.y) >= 3)
        this.stroke.push({ x: e.point.x, y: e.point.y, ll });
      this.renderDraft(this.stroke.map((s) => s.ll));
    } else if (this.drag) {
      this.parts = moveVertex(this.parts, this.drag.p, this.drag.r, this.drag.i, ll);
      this.render(false);
    } else if (this.mode === 'polygon' && this.polygon.length) {
      this.renderDraft([...this.polygon, ll]);
    }
  }

  private up() {
    if (this.mode === 'freehand' && this.stroke.length) {
      const ring = closeRing(simplify(this.stroke, 2).map((s) => s.ll));
      this.stroke = [];
      this.renderDraft([]);
      if (ring) {
        this.setParts([...this.parts, [ring]], true);
        void this.merge();
      }
      return;
    }
    if (this.drag) {
      this.drag = null;
      this.map.dragPan.enable();
      this.render();
      void this.merge();
    }
  }

  private click(e: MapMouseEvent) {
    const ll: Pos = [e.lngLat.lng, e.lngLat.lat];
    if (this.mode === 'probe') return this.onProbe(ll);
    if (this.mode === 'pick') return this.onPick?.(ll);
    if (this.mode === 'polygon') {
      const first = this.polygon[0];
      if (first && this.polygon.length >= 3) {
        const p0 = this.map.project(first);
        if (Math.hypot(p0.x - e.point.x, p0.y - e.point.y) < 10) return this.finishPolygon();
      }
      this.polygon.push(ll);
      this.renderDraft(this.polygon);
      return;
    }
    if (this.mode === 'edit') {
      const hit = this.map.queryRenderedFeatures(e.point, { layers: ['area-fill'] })[0];
      this.selected = hit ? (hit.properties as { p: number }).p : null;
      this.render();
    }
  }

  private finishPolygon() {
    const ring = closeRing(this.polygon);
    this.polygon = [];
    this.renderDraft([]);
    if (ring) {
      this.setParts([...this.parts, [ring]], true);
      void this.merge();
    }
  }

  /** Replaces the parts by their union; the pre-merge state stays in the undo history. Stale results are dropped. */
  private async merge() {
    if (!this.normalize || this.parts.length < 2) return;
    const seq = ++this.mergeSeq;
    const merged = await this.normalize(this.parts).catch(() => null);
    if (!merged || seq !== this.mergeSeq || this.drag) return;
    this.selected = null;
    this.setParts(merged);
  }

  private cancelDraft() {
    this.stroke = [];
    this.polygon = [];
    this.renderDraft([]);
  }

  private cursor() {
    const c = this.map.getCanvas().style;
    c.cursor = this.mode === 'edit' ? '' : 'crosshair';
  }

  private fc() {
    const features: Feature[] = [];
    this.parts.forEach((poly, p) => {
      features.push({
        type: 'Feature',
        properties: { k: 'part', p, sel: p === this.selected },
        geometry: { type: 'Polygon', coordinates: poly },
      });
      if (this.mode === 'edit')
        poly.forEach((ring, r) =>
          ring.slice(0, -1).forEach((pt, i) =>
            features.push({
              type: 'Feature',
              properties: { k: 'v', p, r, i },
              geometry: { type: 'Point', coordinates: pt },
            }),
          ),
        );
    });
    return { type: 'FeatureCollection' as const, features };
  }

  private render(notify = true) {
    (this.map.getSource(SRC) as GeoJSONSource | undefined)?.setData(this.fc() as unknown as SourceData);
    if (notify)
      this.onChange({ parts: this.parts, selected: this.selected, canUndo: this.history.length > 0 });
  }

  private renderDraft(points: Pos[]) {
    const features: Feature[] = points.length
      ? [
          { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: points } },
          ...points.map((pt): Feature => ({
            type: 'Feature',
            properties: {},
            geometry: { type: 'Point', coordinates: pt },
          })),
        ]
      : [];
    (this.map.getSource(DRAFT) as GeoJSONSource | undefined)?.setData({
      type: 'FeatureCollection',
      features,
    });
  }
}
